import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline'

const pricingPlans = [
  {
    name: 'Starter',
    price: 29,
    yearlyPrice: 24,
    description: 'Perfect for solopreneurs and small businesses getting started',
    features: [
      'Monitor up to 10 subreddits',
      'AI lead discovery (100 leads/month)',
      'Basic comment generation',
      'Email notifications',
      '5 keyword sets',
      'Basic analytics',
      'Community support'
    ],
    limitations: [
      'Limited to 50 comments/month',
      'No advanced sentiment analysis',
      'No team collaboration'
    ],
    cta: 'Start Free Trial',
    popular: false
  },
  {
    name: 'Professional',
    price: 97,
    yearlyPrice: 79,
    description: 'Most popular choice for growing businesses and agencies',
    features: [
      'Monitor unlimited subreddits',
      'AI lead discovery (500 leads/month)',
      'Advanced AI comment generation',
      'Multi-channel notifications (Email, Slack, Discord)',
      'Unlimited keyword sets',
      'Advanced sentiment analysis',
      'Performance analytics & ROI tracking',
      'Account health monitoring',
      'Priority support',
      '200 AI-generated comments/month',
      'Custom webhooks',
      'Ban prevention system'
    ],
    limitations: [],
    cta: 'Start Free Trial',
    popular: true
  },
  {
    name: 'Enterprise',
    price: 297,
    yearlyPrice: 247,
    description: 'For large teams and agencies managing multiple clients',
    features: [
      'Everything in Professional',
      'Unlimited AI lead discovery',
      'Unlimited AI-generated comments',
      'Team collaboration tools',
      'Multi-account management',
      'White-label reporting',
      'Custom integrations',
      'Dedicated account manager',
      'Phone support',
      'Custom training',
      'SLA guarantee',
      'Advanced API access'
    ],
    limitations: [],
    cta: 'Contact Sales',
    popular: false
  }
]

const faqs = [
  {
    question: 'Is there a free trial?',
    answer: 'Yes! We offer a 14-day free trial with full access to all features. No credit card required to start.'
  },
  {
    question: 'Can I cancel anytime?',
    answer: 'Absolutely. You can cancel your subscription at any time with no cancellation fees. Your account will remain active until the end of your billing period.'
  },
  {
    question: 'How does Reddit compliance work?',
    answer: 'Our AI follows Reddit\'s terms of service and community guidelines. We use advanced algorithms to ensure natural, helpful comments that add value to discussions.'
  },
  {
    question: 'What if I get banned from Reddit?',
    answer: 'Our ban prevention system has a 99.9% success rate. In the unlikely event of an issue, we provide guidance and best practices to maintain account health.'
  },
  {
    question: 'Do you offer refunds?',
    answer: 'Yes, we offer a 30-day money-back guarantee. If you\'re not satisfied with the results, we\'ll provide a full refund.'
  },
  {
    question: 'Can I upgrade or downgrade my plan?',
    answer: 'Yes, you can change your plan at any time. Upgrades take effect immediately, and downgrades take effect at your next billing cycle.'
  }
]

const testimonialStats = [
  { metric: 'Average ROI', value: '2,400%', description: 'Return on investment within 3 months' },
  { metric: 'Cost per Lead', value: '$0.03', description: 'vs $5.50 average with Google Ads' },
  { metric: 'Customer Acquisition', value: '85%', description: 'Faster than traditional marketing' },
  { metric: 'User Satisfaction', value: '98%', description: 'Would recommend to others' }
]

const PricingPage = () => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly')

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative isolate px-6 pt-14 lg:px-8">
        <div className="mx-auto max-w-4xl py-24 sm:py-32">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl"
            >
              Simple, Transparent
              <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Pricing That Pays for Itself
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-6 text-xl leading-8 text-gray-600 max-w-3xl mx-auto"
            >
              Start free, scale as you grow. Our customers typically see 2,400% ROI within the first 3 months.
              Replace expensive ads with Reddit's organic reach.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-10 flex items-center justify-center"
            >
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-4 py-2 rounded-md font-medium transition-all ${
                    billingCycle === 'monthly'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle('yearly')}
                  className={`px-4 py-2 rounded-md font-medium transition-all ${
                    billingCycle === 'yearly'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  Yearly <span className="text-green-600 font-semibold">(Save 20%)</span>
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-blue-50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            {testimonialStats.map((stat, index) => (
              <motion.div
                key={stat.metric}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="text-3xl font-bold text-blue-600">{stat.value}</div>
                <div className="text-lg font-semibold text-gray-900 mt-1">{stat.metric}</div>
                <div className="text-sm text-gray-600 mt-1">{stat.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`relative rounded-2xl p-8 ${
                  plan.popular
                    ? 'bg-gradient-to-b from-blue-50 to-white border-2 border-blue-200 shadow-xl'
                    : 'bg-white border border-gray-200 shadow-lg'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
                  <p className="text-gray-600 mt-2">{plan.description}</p>

                  <div className="mt-6">
                    <span className="text-5xl font-bold text-gray-900">
                      ${billingCycle === 'monthly' ? plan.price : plan.yearlyPrice}
                    </span>
                    <span className="text-gray-600">/{billingCycle === 'monthly' ? 'month' : 'month'}</span>
                    {billingCycle === 'yearly' && (
                      <div className="text-sm text-green-600 font-semibold">
                        Save ${(plan.price - plan.yearlyPrice) * 12}/year
                      </div>
                    )}
                  </div>

                  <Link
                    to={plan.cta === 'Contact Sales' ? '/contact' : '/signup'}
                    className={`mt-8 w-full inline-flex justify-center py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                      plan.popular
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg hover:scale-105'
                        : 'bg-gray-900 text-white hover:bg-gray-800'
                    }`}
                  >
                    {plan.cta}
                  </Link>
                </div>

                <ul className="mt-8 space-y-3">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                  {plan.limitations.map((limitation, i) => (
                    <li key={i} className="flex items-start">
                      <XMarkIcon className="h-5 w-5 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-500">{limitation}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Money Back Guarantee */}
      <div className="bg-green-50 py-16">
        <div className="mx-auto max-w-4xl text-center px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            30-Day Money-Back Guarantee
          </h2>
          <p className="text-lg text-gray-700 mb-8">
            Try Redd Miner completely risk-free. If you don't see results in 30 days,
            we'll refund every penny. No questions asked.
          </p>
          <div className="flex items-center justify-center space-x-8">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">14 Days</div>
              <div className="text-sm text-gray-600">Free Trial</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">30 Days</div>
              <div className="text-sm text-gray-600">Money Back</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">24/7</div>
              <div className="text-sm text-gray-600">Support</div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="py-24 sm:py-32">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-8">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 rounded-lg p-6"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-700">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Customer Success Stories */}
      <div className="bg-gray-50 py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Real Results from Real Customers
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg p-8 shadow-lg"
            >
              <div className="text-3xl font-bold text-blue-600 mb-2">$127,000</div>
              <div className="text-lg font-semibold text-gray-900 mb-2">Revenue Generated</div>
              <div className="text-sm text-gray-600 mb-4">From $97/month investment</div>
              <p className="text-gray-700 italic">
                "Redd Miner found 300+ leads in our first month. Our SaaS went from $2k to $15k MRR in 6 months."
              </p>
              <div className="mt-4 text-sm">
                <strong>Alex Thompson</strong> - SaaS Founder
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg p-8 shadow-lg"
            >
              <div className="text-3xl font-bold text-blue-600 mb-2">500+</div>
              <div className="text-lg font-semibold text-gray-900 mb-2">New Customers</div>
              <div className="text-sm text-gray-600 mb-4">In 3 months</div>
              <p className="text-gray-700 italic">
                "Our conversion rate from Reddit is 8x higher than Google Ads. ROI is absolutely insane."
              </p>
              <div className="mt-4 text-sm">
                <strong>Maria Garcia</strong> - E-commerce Store Owner
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg p-8 shadow-lg"
            >
              <div className="text-3xl font-bold text-blue-600 mb-2">$0.02</div>
              <div className="text-lg font-semibold text-gray-900 mb-2">Cost per Lead</div>
              <div className="text-sm text-gray-600 mb-4">vs $7.50 with Google Ads</div>
              <p className="text-gray-700 italic">
                "We replaced our entire $10k/month ad budget with Redd Miner. Same results, 99% less cost."
              </p>
              <div className="mt-4 text-sm">
                <strong>David Chen</strong> - Marketing Agency Owner
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="px-6 py-24 sm:px-6 sm:py-32 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Start Your Success Story Today
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-blue-100">
              Join 2,000+ businesses generating millions in revenue from Reddit.
              14-day free trial, no credit card required.
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <Link
                to="/signup"
                className="rounded-lg bg-white px-8 py-4 text-lg font-semibold text-blue-600 shadow-sm hover:bg-blue-50 transition-all duration-200 hover:scale-105"
              >
                Start Free Trial
              </Link>
              <Link to="/contact" className="text-lg font-semibold leading-6 text-white hover:text-blue-100">
                Talk to Sales <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PricingPage